import Country from "country-state-city/lib/country";
import State from "country-state-city/lib/state";
import { t } from "i18next";
import type { JSX } from "react";
import { AppConfig } from "@/common/appConfig";
import type { Occupation } from "@/common/types/occupation";
import { WithDictation } from "@/components/ui/DictationButton";
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { occupationLabel } from "@/lib/occupation-label";
import {
  AccountSettingSchema,
  type AccountSettingSchemaType,
} from "@/schemas/accountSettings.schema";
import { CityCombobox } from "./CityCombobox";
import { CountryCombobox } from "./CountryCombobox";
import { ReadOnlyInputEdit } from "./ReadOnlyInputEdit";
import { SingleFormDialog } from "./SingleFormDialog";
import { StateCombobox } from "./StateCombobox";

interface Props {
  onSubmit: (data: Partial<AccountSettingSchemaType>) => void;
  defaultValues: AccountSettingSchemaType;
  occupations: Occupation[];
  loadingOccupations: boolean;
}

export const ProfileForm = (props: Props): JSX.Element => {
  return (
    <div
      className={
        AppConfig.enableAppRedesign
          ? "-mx-4 -my-1 divide-y divide-v3-border overflow-hidden bg-v3-panel sm:mx-0 sm:my-0 sm:rounded-v3-panel sm:shadow-v3-hairline"
          : "flex flex-col gap-4"
      }
      data-testid="profile-rows"
    >
      <SingleFormDialog
        defaultValues={{ firstname: props.defaultValues.firstname }}
        name={"firstname"}
        schema={AccountSettingSchema().pick({ firstname: true })}
        label={t("settings.profile.firstName")}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.firstName")}`}
        render={(field) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t("settings.profile.firstName")}
            </FormLabel>
            <WithDictation fieldLabel={t("dictation.field.fields.firstName")}>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </WithDictation>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.firstName")}
            value={props.defaultValues.firstname}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({ firstname: value.firstname });
        }}
      />
      <SingleFormDialog
        defaultValues={{ lastname: props.defaultValues.lastname }}
        name={"lastname"}
        schema={AccountSettingSchema().pick({ lastname: true })}
        label={t("settings.profile.lastName")}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.lastName")}`}
        render={(field) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t("settings.profile.lastName")}
            </FormLabel>
            <WithDictation fieldLabel={t("dictation.field.fields.lastName")}>
              <FormControl>
                <Input {...field} />
              </FormControl>
            </WithDictation>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.lastName")}
            value={props.defaultValues.lastname}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({ lastname: value.lastname });
        }}
      />
      <SingleFormDialog
        defaultValues={{ phone: props.defaultValues.phone }}
        name={"phone"}
        schema={AccountSettingSchema().pick({ phone: true })}
        label={t("settings.profile.phone")}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.phone")}`}
        render={(field) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t("settings.profile.phone")}
            </FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.phone")}
            value={props.defaultValues.phone}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({ phone: value.phone });
        }}
      />
      <SingleFormDialog
        name={"occupation"}
        schema={AccountSettingSchema().pick({ occupation: true })}
        label={t("settings.profile.occupation")}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.occupation")}`}
        defaultValues={{
          occupation: props.defaultValues.occupation,
        }}
        render={(field) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t("settings.profile.occupation")}
            </FormLabel>
            <Select
              disabled={props.loadingOccupations}
              value={field.value}
              onValueChange={(value) => {
                const selected = props.occupations.find(
                  (occ) => occ.occupation === value,
                );
                if (selected) {
                  field.onChange(selected.occupation);
                }
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("signUp.form.occupationPlaceholder")}
                  />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {props.occupations.map((occ) => (
                  <SelectItem key={occ.id} value={occ.occupation}>
                    {occupationLabel(occ.occupation, t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.occupation")}
            value={
              props.defaultValues.occupation
                ? occupationLabel(props.defaultValues.occupation, t)
                : props.defaultValues.occupation
            }
          />
        }
        onSubmit={(value) => {
          props.onSubmit({
            occupation: value.occupation,
            occupationId: props.occupations.find(
              (occ) => occ.occupation === value.occupation,
            )?.id,
          });
        }}
      />
      <SingleFormDialog
        name={"country"}
        schema={AccountSettingSchema().pick({ country: true })}
        label={t("settings.profile.country")}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.country")}`}
        defaultValues={{
          country: props.defaultValues.country,
        }}
        render={(field) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t("settings.profile.country")}
            </FormLabel>
            <FormControl>
              <CountryCombobox
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value.value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.country")}
            value={
              Country.getCountryByCode(props.defaultValues.country)?.name || ""
            }
          />
        }
        onSubmit={(value) => {
          props.onSubmit({
            country: value.country,
          });
        }}
      />
      <SingleFormDialog
        name={"state"}
        schema={AccountSettingSchema().pick({ state: true })}
        label={t("settings.profile.state")}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.state")}`}
        defaultValues={{
          state: props.defaultValues.state,
        }}
        render={(field) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t("settings.profile.state")}
            </FormLabel>
            <FormControl>
              <StateCombobox
                countryCode={props.defaultValues.country}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value.value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.state")}
            value={
              State.getStateByCodeAndCountry(
                props.defaultValues.state,
                props.defaultValues.country,
              )?.name ?? ""
            }
          />
        }
        onSubmit={(value) => {
          props.onSubmit({
            state: value.state,
          });
        }}
      />
      <SingleFormDialog
        name={"city"}
        schema={AccountSettingSchema().pick({ city: true })}
        label={t("settings.profile.city")}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.city")}`}
        defaultValues={{
          city: props.defaultValues.city,
        }}
        render={(field) => (
          <FormItem>
            <FormLabel className="sr-only">
              {t("settings.profile.city")}
            </FormLabel>
            <FormControl>
              <CityCombobox
                countryCode={props.defaultValues.country}
                stateCode={props.defaultValues.state}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value.value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.city")}
            value={props.defaultValues.city}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({
            city: value.city,
          });
        }}
      />
    </div>
  );
};
